import { useEffect, useMemo, useState } from "react";
import { Download, Gavel, Loader2 } from "lucide-react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  SCOREME_PARTICULARS,
  buildScoremeMatrix,
  type ScoremeDetailsRow,
} from "../lib/aml/scoremeDetails";
import { getScoremeDetails } from "../services/amlApi";
import { cn } from "../lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  directorName: string;
  directorUuid: string;
  totalLegalCases: number;
};

function CountCell({ value, emphasize }: { value: number; emphasize?: boolean }) {
  const isPositive = value > 0;
  return (
    <TableCell
      className={cn(
        "text-center tabular-nums",
        emphasize && "font-semibold text-foreground",
        !emphasize && isPositive && "font-medium text-red-600",
        !emphasize && !isPositive && "text-muted-foreground"
      )}
    >
      {value}
    </TableCell>
  );
}

export function LegalHistoryDrawer({
  open,
  onOpenChange,
  directorName,
  directorUuid,
  totalLegalCases,
}: Props) {
  const [rows, setRows] = useState<ScoremeDetailsRow[]>([]);
  const [excelS3Url, setExcelS3Url] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !directorUuid) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setExcelS3Url(null);

    void getScoremeDetails(directorUuid)
      .then((data) => {
        if (cancelled) return;
        setRows(data.details ?? []);
        setExcelS3Url(data.excel_s3_url || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setRows([]);
        setExcelS3Url(null);
        setError(
          err instanceof Error
            ? err.message
            : typeof err === "object" && err && "message" in err
              ? String((err as { message: unknown }).message)
              : "Failed to load legal history"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, directorUuid]);

  const matrix = useMemo(() => buildScoremeMatrix(rows), [rows]);

  const handleDownload = () => {
    if (!excelS3Url) return;
    window.open(excelS3Url, "_blank", "noopener,noreferrer");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <SheetHeader className="space-y-3 border-b bg-muted/40 px-6 py-5 pr-14 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <SheetTitle className="flex items-center gap-2">
                <Gavel className="size-4 text-muted-foreground" />
                Legal History
              </SheetTitle>
              <SheetDescription className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {directorName || "Director"}
              </SheetDescription>
            </div>
            <Badge variant="secondary" className="shrink-0 bg-red-100 text-red-700 hover:bg-red-100">
              {totalLegalCases} total {totalLegalCases === 1 ? "case" : "cases"}
            </Badge>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-fit gap-1.5"
            disabled={loading || !excelS3Url}
            onClick={handleDownload}
          >
            <Download className="size-3.5" />
            Download report
          </Button>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading legal history…
            </div>
          ) : error ? (
            <div className="py-16 text-center text-sm text-red-600">{error}</div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table className="min-w-[720px]">
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="min-w-40 font-semibold">Particulars</TableHead>
                    <TableHead className="text-center font-semibold">Total Cases</TableHead>
                    {matrix.yearColumns.map((year) => (
                      <TableHead key={year} className="text-center font-semibold">
                        {year}
                      </TableHead>
                    ))}
                    <TableHead className="text-center font-semibold">All Previous</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {SCOREME_PARTICULARS.map(({ key, label }) => (
                    <TableRow key={key}>
                      <TableCell className="font-semibold">{label}</TableCell>
                      <CountCell value={matrix.rowTotals[key]} emphasize />
                      {matrix.yearColumns.map((year) => (
                        <CountCell
                          key={year}
                          value={matrix.cells[key][String(year)] ?? 0}
                        />
                      ))}
                      <CountCell value={matrix.cells[key].previous ?? 0} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
